import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Text,
  Tooltip,
  UnorderedList,
  visuallyHiddenStyle,
} from "@chakra-ui/react";
import { useCombobox } from "downshift";
import React from "react";
import { RiAddCircleLine } from "react-icons/ri";

// Chakra's Tooltip sets aria-describedby on its trigger only while open, so the disabled reason is linked here
// instead, to also reach touch users and screen readers that don't move focus.
const DescribedButton = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { descriptionId: string | undefined }
>(({ descriptionId, ...props }, ref) => (
  <Button {...props} ref={ref} aria-describedby={descriptionId} />
));

interface BaseType {
  [key: string]: any;
}

type MultiSelectOrderableListProps<T> = {
  items: readonly T[];
  selectedItems: readonly T[];
  keyAttr: string;
  searchableFields: string[];
  /** Accessible name of the search input and its results list. */
  label: string;
  /** Renders the label for screen readers only. */
  hideLabel?: boolean;
  placeholder?: string;
  /** Shown and announced when the search has no results. */
  noResultsText?: string;
  button: {
    label: string;
    disabled?: boolean;
    /** Shown in a tooltip and linked as the button's description while it is disabled. */
    disabledReason?: string;
  };
  renderListItem: (item: T) => React.ReactElement;
  renderSelectedItems: (items: readonly T[]) => React.ReactElement;
  onSelectedItemChange: (item: T) => void;
  onIsDropdownOpenChange?: (isOpen: boolean) => void;
};

export function MultiSelectOrderableList<T extends BaseType>({
  items,
  selectedItems,
  keyAttr,
  searchableFields,
  label,
  hideLabel = false,
  placeholder,
  noResultsText,
  button,
  onSelectedItemChange,
  renderListItem,
  renderSelectedItems,
  onIsDropdownOpenChange,
}: MultiSelectOrderableListProps<T>) {
  const [dropdownItems, setDropdownItems] = React.useState<readonly T[]>(items);
  const [query, setQuery] = React.useState<string>();
  const toggleButtonRef = React.useRef<HTMLButtonElement>(null);
  // the search input is hidden when the menu closes, so focus has to be moved back to the toggle button.
  const returnFocusRef = React.useRef(false);
  const disabledReasonId = React.useId();

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    onInputValueChange({ inputValue }) {
      setQuery(inputValue || undefined);
    },
    items: [...dropdownItems],
    onSelectedItemChange: ({ selectedItem }) => {
      setQuery(undefined);
      if (!selectedItem) {
        return;
      }

      onSelectedItemChange(selectedItem);
    },
    onIsOpenChange: (changes) => {
      if (
        changes.isOpen !== undefined &&
        onIsDropdownOpenChange !== undefined
      ) {
        onIsDropdownOpenChange(changes.isOpen);
      }

      if (changes.isOpen === false) {
        setQuery(undefined);
        // leave focus alone when the user moved it elsewhere (e.g. blur by clicking or tabbing away).
        returnFocusRef.current =
          changes.type === useCombobox.stateChangeTypes.InputKeyDownEscape ||
          changes.type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
          changes.type === useCombobox.stateChangeTypes.ItemClick;
      }
    },
    // "No results" is announced by the visible status below, so downshift only announces result counts.
    getA11yStatusMessage: ({ isOpen, resultCount, previousResultCount }) =>
      isOpen && resultCount > 0 && resultCount !== previousResultCount
        ? `${resultCount} result${
            resultCount === 1 ? " is" : "s are"
          } available, use up and down arrow keys to navigate. Press Enter to select.`
        : "",
    selectedItem: null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            highlightedIndex: state.highlightedIndex,
            inputValue: "", // don't add the item string as input value at selection.
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: "", // don't add the item string as input value at selection.
          };
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          // the query is cleared on close, so clear the input too rather than showing it over the full list.
          return { ...changes, inputValue: "" };
        case useCombobox.stateChangeTypes.InputClick:
          // clicking into the open input (e.g. to move the cursor) shouldn't close the search.
          return { ...changes, isOpen: state.isOpen || changes.isOpen };
        default:
          return changes;
      }
    },
  });

  const getItemsFilter = React.useCallback(
    (inputValue: string) => {
      return function itemsFilter(item: T) {
        const fieldIncludesInput = !!searchableFields.find((field) =>
          item[field]?.toLowerCase().includes(inputValue.toLowerCase())
        );
        return (
          !selectedItems.includes(item) && (fieldIncludesInput || !inputValue)
        );
      };
    },
    [selectedItems, searchableFields]
  );

  React.useEffect(() => {
    if (!isOpen && returnFocusRef.current) {
      returnFocusRef.current = false;
      toggleButtonRef.current?.focus();
    }
  }, [isOpen]);

  React.useEffect(() => {
    setDropdownItems(items.filter(getItemsFilter(query || "")));
  }, [query, getItemsFilter, items]);

  const isEmpty = selectedItems.length === 0;
  const isButtonDisabled = button.disabled ?? false;
  const showDisabledReason = isButtonDisabled && !!button.disabledReason;
  const showNoResults = isOpen && !!query && dropdownItems.length === 0;
  // the input sits outside the listbox, which may only contain options, so the menu ref goes on the wrapper:
  // downshift closes the menu on clicks outside its tracked elements.
  const { ref: menuRef, ...menuProps } = getMenuProps();
  const addButton = (
    <Box border="2px dashed" borderColor="gray.300" borderRadius="lg">
      <Tooltip
        label={button.disabledReason}
        hasArrow
        placement="top"
        isDisabled={!showDisabledReason}
      >
        <DescribedButton
          {...getToggleButtonProps({
            ref: toggleButtonRef,
            onClick: isButtonDisabled ? preventDownshiftDefault : undefined,
          })}
          descriptionId={showDisabledReason ? disabledReasonId : undefined}
          padding={3}
          type="button"
          variant="link"
          width="100%"
          display="flex"
          alignItems="center"
          color="gray.700"
          gap={2}
          tabIndex={0}
          // aria-disabled instead of isDisabled keeps the button focusable, so focus can return to it when the
          // menu closes and keyboard users can reach the tooltip.
          aria-disabled={isButtonDisabled}
        >
          <Icon as={RiAddCircleLine} />
          {button.label}
        </DescribedButton>
      </Tooltip>
      {showDisabledReason && (
        // hidden so it's only read as the button's description, not again as page text.
        <span id={disabledReasonId} hidden>
          {button.disabledReason}
        </span>
      )}
    </Box>
  );
  return (
    <>
      <Box
        as="label"
        {...getLabelProps()}
        sx={hideLabel ? visuallyHiddenStyle : undefined}
      >
        {label}
      </Box>
      <Box
        ref={menuRef}
        // keep focus in the search input when clicking around it, e.g. on the search icon.
        onMouseDown={(event: React.MouseEvent) => {
          if (!(event.target instanceof HTMLInputElement)) {
            event.preventDefault();
          }
        }}
        visibility={isOpen ? "visible" : "hidden"}
        height={isOpen ? "inherit" : "0px"}
        bg="white"
        borderRadius="md"
      >
        <Box padding={4}>
          <InputGroup>
            <InputLeftElement children={<SearchIcon color="gray" />} />
            <Input placeholder={placeholder} {...getInputProps()} />
          </InputGroup>
        </Box>
        <Text role="status" paddingX={4} paddingBottom={showNoResults ? 4 : 0}>
          {showNoResults ? noResultsText : null}
        </Text>
        <UnorderedList {...menuProps} listStyleType="none" margin="0">
          {isOpen &&
            dropdownItems.map((item, index) => (
              <ListItem
                {...getItemProps({ item, index })}
                key={item[keyAttr]}
                backgroundColor={
                  highlightedIndex === index ? "gray.200" : "white"
                }
                padding={3}
                cursor="pointer"
                borderColor="gray.300"
                borderStyle="solid"
                borderBottomWidth="1px"
                _last={{ borderBottomWidth: "0" }}
                fontSize="sm"
              >
                <Flex direction="column" height="100%">
                  {renderListItem(item)}
                </Flex>
              </ListItem>
            ))}
        </UnorderedList>
      </Box>
      {/* The add button keeps one place in the tree, so it isn't remounted (and focus isn't lost) when the
      first item is added. With no items, the outer Flex draws the card around it instead. */}
      <Flex
        direction="column"
        gap={3}
        visibility={isOpen ? "hidden" : "visible"}
        {...(isEmpty ? emptyCardProps : {})}
      >
        <Flex
          padding={isEmpty ? 0 : 4}
          gap={3}
          bg="white"
          borderRadius="lg"
          direction="column"
        >
          {renderSelectedItems(selectedItems)}
        </Flex>
        {addButton}
      </Flex>
    </>
  );
}

const emptyCardProps = {
  padding: 4,
  bg: "white",
  borderRadius: "lg",
} as const;

function preventDownshiftDefault(event: React.MouseEvent) {
  // stops downshift from opening the menu while the button is disabled.
  (
    event.nativeEvent as MouseEvent & { preventDownshiftDefault?: boolean }
  ).preventDownshiftDefault = true;
}
